// @flow

import * as React from 'react';
import classNames from 'classnames';
import colorUtils from 'color';
import useSheet from 'app/jss';
import Avatar from 'app/components/commons/Avatar';
import PlayersCountDisplay from './PlayersCountDisplay';
import UserStatsCols from './UserStatsCols';

type Props = {
  avatar?: Object,
  compact?: boolean,
  displayPlayersCount?: boolean,
  entryCount?: number,
  highlight?: boolean,
  isUltimateChampion?: boolean,
  name: string,
  onStatusSelect: () => void,
  playersCount?: Array<Object>,
  prize?: string,
  rank: number,
  score: number,
  scoreProjected?: number,
  sheet: Object,
  showProjected: boolean,
  statusFilters: Object
};

const defaultProps = {
  avatar: undefined,
  compact: false,
  displayPlayersCount: false,
  entryCount: 0,
  highlight: false,
  isUltimateChampion: false,
  playersCount: undefined,
  prize: undefined,
  scoreProjected: 0
};

function UserStats({
  avatar,
  compact,
  displayPlayersCount,
  entryCount,
  highlight,
  isUltimateChampion,
  name,
  onStatusSelect,
  playersCount,
  prize,
  rank,
  score,
  scoreProjected,
  sheet: { classes },
  showProjected,
  statusFilters
}: Props) {
  return (
    <div className={classNames(classes.userStatsBox, { highlight })}>
      <div className={classNames(classes.userStatsHeader, { compact })}>
        <div className={classes.user}>
          <Avatar
            avatar={avatar}
            size="small"
            className={classes.avatar}
            hasCrown={isUltimateChampion}
          />
          <div className={classes.userName}>{name}</div>
        </div>
        {displayPlayersCount && (
          <div className={classes.playerCountWrapper}>
            <PlayersCountDisplay
              data={playersCount}
              onSelect={onStatusSelect}
              statusFilters={statusFilters}
            />
          </div>
        )}
      </div>
      <UserStatsCols
        entryCount={entryCount}
        prize={prize}
        rank={rank}
        score={score}
        scoreProjected={scoreProjected}
        showProjected={showProjected}
      />
    </div>
  );
}

UserStats.defaultProps = defaultProps;

function applyStyles(theme: Theme) {
  return {
    avatar: {
      borderColor: colorUtils(theme.color.white)
        .alpha(0.25)
        .toString(),
      borderStyle: 'solid',
      borderWidth: 2
    },
    playerCountWrapper: {
      alignItems: 'center',
      display: 'flex',
      borderColor: colorUtils(theme.color.white)
        .alpha(0.25)
        .toString(),
      borderStyle: 'solid',
      borderTopWidth: 0,
      borderRightWidth: 0,
      borderBottomWidth: 1,
      borderLeftWidth: 0,
      flexBasis: 'auto',
      flexGrow: 0,
      flexShrink: 0,
      paddingTop: theme.spacing.tiny,
      paddingBottom: theme.spacing.tiny
    },
    tally: {
      fontSize: theme.fontSize.xtiny
    },
    user: {
      alignItems: 'center',
      borderColor: colorUtils(theme.color.white)
        .alpha(0.25)
        .toString(),
      borderStyle: 'solid',
      borderTopWidth: 0,
      borderRightWidth: 0,
      borderBottomWidth: 1,
      borderLeftWidth: 0,
      display: 'flex',
      flexGrow: 1,
      flexShrink: 1,
      flexBasis: 0,
      overflow: 'hidden',
      paddingTop: theme.spacing.tiny,
      paddingRight: theme.spacing.tiny,
      paddingBottom: theme.spacing.tiny,
      paddingLeft: theme.spacing.tiny,
      '$userStatsHeader.compact &': {
        flexBasis: 'auto'
      }
    },
    userStatsBox: {
      backgroundColor: theme.color.basis2,
      borderColor: theme.color.basis2,
      borderStyle: 'solid',
      borderTopWidth: 0,
      borderRightWidth: 0,
      borderBottomWidth: 1,
      borderLeftWidth: 0,
      color: theme.color.white,
      display: 'flex',
      flexBasis: 0,
      flexDirection: 'column',
      flexGrow: 1,
      flexShrink: 1,
      minWidth: 0, // flexbox hack
      '&.highlight': {
        backgroundColor: theme.color.primary
      },
      '&:not(:first-of-type)': {
        borderLeftWidth: 1
      }
    },
    userStatsHeader: {
      alignItems: 'stretch',
      display: 'flex',
      flexGrow: 1,
      justifyContent: 'space-between',
      '&.compact': {
        flexDirection: 'column'
      }
    },
    userImage: {
      backgroundColor: theme.color.white,
      borderRadius: '50%',
      height: 40,
      width: 40
    },
    userName: {
      overflow: 'hidden',
      paddingLeft: theme.spacing.tiny,
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    }
  };
}

export default useSheet(applyStyles)(UserStats);
